import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, GeoJSON, LayersControl, Rectangle } from 'react-leaflet';
import { LinearProgress, Box } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton } from '@mui/material';
import axios from 'axios';
import { debounce } from 'lodash';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import LegendMap from './map_legend.js';
import { useMapSync } from './map_syncro.js';

const MapLeft = ({ cqlSevere, cqlFecha, mapLeftLayer, issueTime, weekNumber, selectedTime }) => {
  const geoJsonRef = useRef(null);
  const mapRef = useRef(null);
  const [hasData, setHasData] = useState(true);

  // Estado de carga
  const [loading, setLoading] = useState(true);

  // Estado para controlar la visibilidad de la leyenda
  const [showLegend, setShowLegend] = useState(false); 

  // Función para mostrar u ocultar la leyenda  
  const handleToggleLegend = () => {
    setShowLegend(!showLegend);
  };

  // Sincronización
  const { mapState, updateMapState } = useMapSync();

// Función para descargar el GeoJSON visible
const handleDownloadGeoJSON = () => {
  if (geoJsonRef.current) {
    const geojsonData = geoJsonRef.current.toGeoJSON();
    const blob = new Blob([JSON.stringify(geojsonData)], { type: 'application/json' });
    // Construir el nombre del archivo basado en el tipo de capa y la fecha
    const layerType = mapLeftLayer.includes('025') ? 'EU_025' : 'EU_0125';
    const fileName = `geojson_${layerType}_${cqlFecha}.geojson`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  }
};

// Función para resetear la vista del mapa
const handleResetView = useCallback(() => {
  if (mapRef.current) {
    const map = mapRef.current;
    map.fitBounds([[73, -27], [33, 45]]);
  }
}, []);

//Funvion para mejorar la fluidez y renderizacion del mapa cuando el usuario mueva y hga zoom bruscamente sobre el mapa:
  const handleMoveEnd = debounce(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      const center = map.getCenter();
      const zoom = map.getZoom();

      // Actualiza el estado del mapa en tu aplicación sincronizada
      updateMapState({
        center,
        zoom,
      });
    }
  }, 150); // ms de debounce
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;

      // Agrega el evento moveend con la función debounced
      map.on('moveend', handleMoveEnd);

      // Limpieza: quita el evento cuando se desmonte el componente
      return () => {
        map.off('moveend', handleMoveEnd);
      };
    }
  }, [handleMoveEnd, updateMapState]);
// useEffect(() => {
//   if (mapRef.current) {
//     const map = mapRef.current;

//     // Evento moveend para sincronización al finalizar el movimiento del mapa
//     const handleMoveEnd = () => {
//       updateMapState({
//         center: map.getCenter(),
//         zoom: map.getZoom(),
//       });
//     };

//     map.on('moveend', handleMoveEnd);

//     return () => {
//       map.off('moveend', handleMoveEnd);
//     };
//   }
// }, [mapRef, updateMapState]);

//   useEffect(() => {
//     if (mapRef.current) {
//       const map = mapRef.current;
//       map.setView(mapState.center, mapState.zoom);
//     }
//   }, [mapState.center, mapState.zoom]);

  // Estilos
  const styleFeature = useMemo(() => {
    return (feature, layerOrder) => {
      const risk = feature.properties.CONVECTIVE_MATRIX_RISK;
      const opacityLevels = [0.65, 0.65, 0.65, 0.65, 0.65]; // Define transparency levels for layers
      const opacity = opacityLevels[layerOrder % opacityLevels.length]; // Cycle through opacity levels

      switch (risk) {
        case 5:
          return {
            color: '#000000',
            fillColor: '#6A5ACD',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 50000,
          };
        case 4:
          return {
            color: '#000000',
            fillColor: '#DC143C',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 40000,
          };
        case 3:
          return {
            color: '#000000',
            fillColor: '#FF8C00',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 30000,
          };
        case 2:
          return {
            color: '#000000',
            fillColor: '#ffff00',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 20000,
          };
        case 1:
          return {
            color: '#000000',
            fillColor: '#2E8B57',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 10000,
          };
        default:
          return {
            color: '#000000',
            fillColor: '#FFFFFF',
            fillOpacity: 0,
            opacity: opacity,
            weight: 0,
            zIndex: 1,
          };
      }
    };
  }, []);
   // Estilos CNN
  const styleFeatureCNN = useMemo(() => {
    return (feature, layerOrder) => {
      const risk = feature.properties.CONVECTIVE_MATRIX_RISK;
      const opacityLevels = [0.65, 0.65, 0.65, 0.65, 0.65]; // Define transparency levels for layers
      const opacity = opacityLevels[layerOrder % opacityLevels.length]; // Cycle through opacity levels

      switch (risk) {
        case 4:
          return {
            color: '#000000',
            fillColor: '#6A5ACD',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 50000,
          };
        case 3:
          return {
            color: '#000000',
            fillColor: '#DC143C',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 40000,
          };
        case 2:
          return {
            color: '#000000',
            fillColor: '#FF8C00',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 30000,
          };
        case 1:
          return {
            color: '#000000',
            fillColor: '#ffff00',
            fillOpacity: opacity,
            opacity: opacity,
            weight: 0,
            zIndex: 20000,
          };
        default:
          return {
            color: '#000000',
            fillColor: '#FFFFFF',
            fillOpacity: 0,
            opacity: opacity,
            weight: 0,
            zIndex: 1,
          };
      }
    };
  }, []);
  // Estilo dinamico en funcion de la capa seleccionada
  const DynamicStyle = useMemo(() => {
    return mapLeftLayer === 'CNN_HISTORICAL:EU_025_live_demo'
      ? styleFeatureCNN
      : styleFeature;
  }, [mapLeftLayer, styleFeatureCNN, styleFeature]);

  //VALORES PARA ASIGNAR PROBABILIDADES EN LA CQL_FILTER
  // const filtroFP = `((CONVECTIVE_MATRIX_RISK = 1 AND PROBABILITY = 'FP 0.025' ) OR (CONVECTIVE_MATRIX_RISK = 2 AND PROBABILITY = 'FP 0.02') OR (CONVECTIVE_MATRIX_RISK = 3 AND PROBABILITY = 'FP 0.03')  OR (CONVECTIVE_MATRIX_RISK = 4 AND PROBABILITY = 'FP 0.03')  OR (CONVECTIVE_MATRIX_RISK = 5 AND PROBABILITY = 'FP 0.02'))`;

  const geoJsonUrls = useMemo(() => ({
    "VALIDATION_PLAN_v2:EU_025_historical_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/validation_plan?service=WFS&version=1.1.0&request=GetFeature&typeName=VALIDATION_PLAN_v2:EU_025_historical_view&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/validation_plan_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=VALIDATION_PLAN_v2:EU_025_historical_view&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    // "CNN_HISTORICAL:EU_025_live_demo": {
    //   primary: `https://geoserver-aimethods.uc3m.es/geoserver/CNN_HISTORICAL/wfs?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_025_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
    //   backup:  `https://geoserver-aimethods.uc3m.es/geoserver/CNN_HISTORICAL/wfs?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_025_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`
    // },
    "VALIDATION_PLAN_v2:EU_0125_historical_view": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/validation_plan?service=WFS&version=1.1.0&request=GetFeature&typeName=VALIDATION_PLAN_v2:EU_0125_historical_view&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
      backup:  `${process.env.REACT_APP_API_URL}/proxy/wfs/validation_plan_backup?service=WFS&version=1.1.0&request=GetFeature&typeName=VALIDATION_PLAN_v2:EU_0125_historical_view&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
    },
  }), [cqlFecha, issueTime, weekNumber]);

  // Función para realizar el fetch con fallback
  const fetchWithFallback = useCallback(async (urlObj, signal) => {
    if (!urlObj || !urlObj.primary || !urlObj.backup) return null;
    try {
      const resp = await axios.get(urlObj.primary, { signal });
      if (resp.status === 200) return resp.data;
    } catch (err) {
      console.warn(err.message);
    }
    const backupResp = await axios.get(urlObj.backup, { signal });
    return backupResp.data;
  }, []);

  // Cargar datos GeoJSON
  const loadGeoJson = useCallback(async (urlObj) => {
    const controlador = new AbortController();
    setLoading(true); // Comienza la carga
    try {
      const data = await fetchWithFallback(urlObj, controlador.signal);
      const features = data.features;
      setHasData(features.length > 0); // Actualiza hasData basado en la existencia de características

      if (geoJsonRef.current) {
        if (mapLeftLayer !== "ENAIRE:SECTORS") {
          features.sort((b, a) => b.properties.CONVECTIVE_MATRIX_RISK - a.properties.CONVECTIVE_MATRIX_RISK);
        }
        requestAnimationFrame(() => { // el navegador optimiza la animacion del mapa
          geoJsonRef.current.clearLayers();// Limpia los datos antiguos
          geoJsonRef.current.addData(features); // Añade los nuevos datos
        });
      }
    } catch (error) {
      console.error('Error fetching GeoJSON data:', error);
    } finally {
      setLoading(false); // Termina la carga
    }
    return () => {
      controlador.abort();
    }
  }, [mapLeftLayer, fetchWithFallback]);

  useEffect(() => {
    if (mapLeftLayer) {
      loadGeoJson(geoJsonUrls[mapLeftLayer]);
    }
  }, [mapLeftLayer, geoJsonUrls]);
 

  // const rectanguloByLayerName = useMemo(() => ({
  //   // "VALIDATION_PLAN:EU_025_historical_view": [[73, -27], [33, 45]],
  //   // "VALIDATION_PLAN:EU_0125_historical_view": [[60, -10], [36, 25]],
  // //  "VALIDATION_PLAN_v2:EU_025_historical_view": [[73, -27], [33, 45]],
  //   "CNN_HISTORICAL:EU_025_live_demo": [[73, -27], [33, 45]],
  //   "VALIDATION_PLAN_v2:EU_0125_historical_view": [[60, -10], [36, 25]],
  //   // "ENAIRE:ENAIRE_icon_live_demo": [[34, -12], [46, 7]],
  //   // "ENAIRE:ENAIRE_ecmwf_live_demo": [[23, -24], [48, 12]],
  // }), []);

  const rectBounds = useMemo(() => {
    if (!mapLeftLayer) return null;
    return mapLeftLayer.includes('0125')
      ? [[36, -10], [60, 25]]  // bounds para 0125
      : [[33, -27], [73, 45]];  // bounds para 025
  }, [mapLeftLayer]);

  useEffect(() => {
    if (rectBounds && mapRef.current) {
      mapRef.current.fitBounds(rectBounds);
    }
  }, [rectBounds]);
  
  // Creamos un renderizador en Canvas (se memorizan para no recrearlo en cada render)
  const canvasRenderer = useMemo(() => L.canvas(), []);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
    {loading && (
      <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1000 }}>
        <LinearProgress />
      </Box>
    )}
      <MapContainer ref={mapRef} center={[48, 7.5]} zoom={3.75} minZoom={2.65} maxZoom={16} style={{ height: "45vh", width: "100%" }} zoomSnap={0.35} wheelPxPerZoomLevel={100} zoomDelta={1.5}>
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="OpenStreetMap.Mapnik">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
              attribution=''
            />
          </LayersControl.BaseLayer>
          <LayersControl.Overlay checked name="GeoJSON Layer">
            <GeoJSON key={mapLeftLayer} data={[]} ref={geoJsonRef} style={DynamicStyle} chunkedLoading={true} renderer={canvasRenderer}/>
          </LayersControl.Overlay>
          {rectBounds && (
                <Rectangle
                  key={mapLeftLayer}
                  bounds={rectBounds}
                  pathOptions={{ color: '#141414', weight: 1, dashArray: '3,5', fillOpacity: 0 }}
                  interactive={false}
                />
          )}
        </LayersControl>
      </MapContainer>
        {/* Botón de descarga */}
        <IconButton onClick={handleDownloadGeoJSON} style={{ position: 'absolute', top: 0, right: 27.5, zIndex: 1000 }}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        {/* Botón para resetear vista */}
        <IconButton onClick={handleResetView} style={{ position: 'absolute', top: 0, right: 2.5, zIndex: 1000 }}>
          <HomeIcon fontSize="small" />
        </IconButton>
    {/* Botón para mostrar/ocultar la leyenda */}
    <IconButton onClick={handleToggleLegend} style={{ position: 'absolute', bottom: 0, right: 2.5, zIndex: 1000 }}>
      <InfoIcon fontSize="small" />
    </IconButton>
    {/* Añadir la leyenda */}
    { showLegend && <LegendMap style={{zIndex:0}}/>}
      {!hasData && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          textAlign: 'center',
        }}>
        <p>No data available for this Issue Time until <strong>{cqlFecha.split(' ')[0]} {selectedTime} UTC</strong>.<br />Move the slider!</p>
        </div>
      )}
        </Box>


  );
};

export default MapLeft;
